import { useState, useEffect, useCallback, useRef } from "react";
import { CreditCard, Loader2, Euro, CheckCircle, AlertCircle, RefreshCw, FileText, XCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLLING_INTERVAL = 3000; // Poll every 3 seconds
const AUTO_CLOSE_DELAY = 5000; // Auto close after 5 seconds

export default function PaymentPage() {
  const [amount, setAmount] = useState("");
  const [platformFee, setPlatformFee] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [polling, setPolling] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = "Payment | GeekTickets POS";
  }, []);
  const [cancelling, setCancelling] = useState(false);
  const pollingRef = useRef(null);
  const autoCloseRef = useRef(null);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setAmount(value);
    }
  };

  const handleFeeChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setPlatformFee(value);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
      }
    };
  }, []);

  const startAutoClose = useCallback(() => {
    // Clear any existing auto-close timer
    if (autoCloseRef.current) {
      clearTimeout(autoCloseRef.current);
    }
    
    // Set new auto-close timer
    autoCloseRef.current = setTimeout(() => {
      setShowModal(false);
      setLastTransaction(null);
    }, AUTO_CLOSE_DELAY);
  }, []);

  const pollTransactionStatus = useCallback(async (transactionId) => {
    try {
      const response = await axios.post(`${API}/transactions/${transactionId}/poll`);
      const { status, updated } = response.data;
      
      if (updated || status === "completed" || status === "failed" || status === "refunded") {
        setLastTransaction(prev => ({ ...prev, status }));
        
        if (status === "completed") {
          toast.success("Payment completed successfully!");
          setPolling(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          startAutoClose();
        } else if (status === "failed") {
          toast.error("Payment failed or was declined");
          setPolling(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          startAutoClose();
        }
      }
    } catch (error) {
      console.error("Polling error:", error);
    }
  }, [startAutoClose]);

  const startPolling = useCallback((transactionId) => {
    setPolling(true);
    pollingRef.current = setInterval(() => {
      pollTransactionStatus(transactionId);
    }, POLLING_INTERVAL);
    
    // Stop polling after 5 minutes
    setTimeout(() => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPolling(false);
      }
    }, 5 * 60 * 1000);
  }, [pollTransactionStatus]);

  const handleVoidTransaction = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/terminal/cancel`);
      
      // Stop polling
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setPolling(false);
      
      // Update transaction status
      setLastTransaction(prev => ({ ...prev, status: "voided" }));
      toast.success("Transaction voided successfully");
      
      // Start auto-close timer
      startAutoClose();
    } catch (error) {
      console.error("Void error:", error);
      const errorMessage = error.response?.data?.detail || "Failed to void transaction";
      toast.error("Void failed", { description: errorMessage });
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amountNum = parseFloat(amount);
    const feeNum = parseFloat(platformFee);

    if (!amountNum || amountNum <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (isNaN(feeNum) || feeNum < 0) {
      toast.error("Please enter a valid platform fee");
      return;
    }

    if (feeNum > amountNum) {
      toast.error("Platform fee cannot exceed the total amount");
      return;
    }

    if (!note.trim()) {
      toast.error("Please enter a note");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        amount: amountNum,
        platform_fee: feeNum,
        note: note.trim()
      };

      const response = await axios.post(`${API}/payments`, payload);

      setLastTransaction(response.data);
      setShowModal(true);
      
      // Clear any existing auto-close timer when new transaction starts
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }

      // Start polling for status updates
      if (response.data.transaction_id) {
        startPolling(response.data.transaction_id);
      }

      // Reset form after successful submission
      setAmount("");
      setPlatformFee("");
      setNote("");

    } catch (error) {
      console.error("Payment error:", error);
      const errorMessage = error.response?.data?.detail || "Failed to initiate payment";
      toast.error("Payment failed", { description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const isValidFee = platformFee === "" || (parseFloat(platformFee) >= 0 && parseFloat(platformFee) <= parseFloat(amount || 0));
  
  const isTransactionComplete = lastTransaction && 
    (lastTransaction.status === "completed" || 
     lastTransaction.status === "failed" || 
     lastTransaction.status === "voided");

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 lg:p-12">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-1">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            Initiate Payment
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Enter amount and platform fee
          </p>
        </div>

        {/* Payment Form */}
        <Card className="border-2">
          <CardHeader className="text-center pb-3 sm:pb-4 px-4 sm:px-6">
            <CardTitle className="flex items-center justify-center gap-2 text-base sm:text-lg">
              <Euro className="w-4 h-4 sm:w-5 sm:h-5" />
              Transaction Amount
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Currency: EUR (Euro)
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              {/* Main Amount Input */}
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-sm font-medium">
                  Total Amount <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-2xl sm:text-3xl md:text-4xl text-muted-foreground font-mono">
                    €
                  </span>
                  <Input
                    id="amount"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={handleAmountChange}
                    disabled={loading}
                    data-testid="amount-input"
                    className="h-16 sm:h-20 md:h-24 pl-10 sm:pl-12 text-2xl sm:text-3xl md:text-4xl font-mono text-center border-2 focus-visible:ring-2 focus-visible:ring-primary"
                  />
                </div>
              </div>

              {/* Platform Fee Input */}
              <div className="space-y-2">
                <Label htmlFor="platformFee" className="text-sm font-medium">
                  Platform Fee <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-base sm:text-lg text-muted-foreground font-mono">
                    €
                  </span>
                  <Input
                    id="platformFee"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={platformFee}
                    onChange={handleFeeChange}
                    disabled={loading}
                    data-testid="platform-fee-input"
                    className={`h-12 sm:h-14 pl-9 sm:pl-10 text-lg sm:text-xl font-mono text-center border focus-visible:ring-1 ${
                      !isValidFee ? "border-destructive focus-visible:ring-destructive" : ""
                    }`}
                  />
                </div>
                {!isValidFee && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Fee cannot exceed total amount
                  </p>
                )}
              </div>

              {/* Note Input */}
              <div className="space-y-2">
                <Label htmlFor="note" className="text-sm font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Note <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="note"
                  placeholder="Add a note for this transaction..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={loading}
                  data-testid="note-input"
                  className="min-h-[80px] resize-none text-sm"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {note.length}/500
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                disabled={loading || !amount || !platformFee || !isValidFee || !note.trim()}
                data-testid="pay-button"
                className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Initiate Payment
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Transaction Status Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-[90vw] sm:max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {lastTransaction?.status === "completed" ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Payment Completed
                </>
              ) : lastTransaction?.status === "failed" ? (
                <>
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  Payment Failed
                </>
              ) : lastTransaction?.status === "voided" ? (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  Payment Voided
                </>
              ) : (
                <>
                  <RefreshCw className={`w-5 h-5 text-yellow-600 ${polling ? "animate-spin" : ""}`} />
                  Payment In Progress
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {lastTransaction && (
            <div className="space-y-4">
              <div className={`rounded-lg p-4 ${
                lastTransaction.status === "completed" 
                  ? "bg-green-50 border border-green-200" 
                  : lastTransaction.status === "failed" || lastTransaction.status === "voided"
                  ? "bg-red-50 border border-red-200"
                  : "bg-yellow-50 border border-yellow-200"
              }`}>
                <div className={`text-sm space-y-2 ${
                  lastTransaction.status === "completed" 
                    ? "text-green-700" 
                    : lastTransaction.status === "failed" || lastTransaction.status === "voided"
                    ? "text-red-700"
                    : "text-yellow-700"
                }`}>
                  <div className="flex justify-between">
                    <span>Amount:</span>
                    <span className="font-mono font-medium">€{lastTransaction.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Platform Fee:</span>
                    <span className="font-mono font-medium">€{lastTransaction.platform_fee?.toFixed(2)}</span>
                  </div>
                  {lastTransaction.note && (
                    <div className="flex justify-between">
                      <span>Note:</span>
                      <span className="italic text-right max-w-[60%] truncate">{lastTransaction.note}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <span className="font-medium capitalize">{lastTransaction.status?.replace("_", " ")}</span>
                  </div>
                </div>
              </div>
              
              {/* Void Button for in-progress transactions */}
              {!isTransactionComplete && (
                <Button
                  variant="destructive"
                  onClick={handleVoidTransaction}
                  disabled={cancelling}
                  data-testid="void-button"
                  className="w-full"
                >
                  {cancelling ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Voiding...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Void Transaction
                    </>
                  )}
                </Button>
              )}
              
              {/* Auto-close message for completed transactions */}
              {isTransactionComplete && (
                <p className="text-xs text-center text-muted-foreground">
                  This dialog will close automatically in a few seconds
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
